/**
 * Chat presentational UI (WP29).
 */

import type { ConversationSummary } from "@carago/shared";
import { Link } from "react-router-dom";
import { researchJobPath } from "../lib/chat.js";

export type ChatBubble =
  | {
      id: string;
      kind: "user";
      content: string;
    }
  | {
      id: string;
      kind: "assistant";
      content: string;
      streaming?: boolean;
    }
  | {
      id: string;
      kind: "tool";
      name: string;
      ok: boolean;
      summary: string;
      researchJobId?: string | null;
    };

export type ChatViewState =
  | "idle"
  | "loading"
  | "ready"
  | "error"
  | "stream_error";

export type ChatViewProps = {
  state: ChatViewState;
  errorMessage?: string | null;
  conversations: ConversationSummary[];
  activeConversationId: string | null;
  bubbles: ChatBubble[];
  draft: string;
  sending: boolean;
  onDraftChange: (value: string) => void;
  onSend: () => void;
  onSelectConversation: (id: string | null) => void;
  onRetry?: () => void;
};

export function ChatView({
  state,
  errorMessage,
  conversations,
  activeConversationId,
  bubbles,
  draft,
  sending,
  onDraftChange,
  onSend,
  onSelectConversation,
  onRetry,
}: ChatViewProps) {
  const showThread =
    state === "ready" ||
    state === "idle" ||
    state === "stream_error" ||
    (state === "loading" && bubbles.length > 0);

  return (
    <div
      data-testid="chat-view"
      className="mx-auto flex h-[calc(100dvh-7.5rem)] max-w-6xl gap-4"
    >
      <aside
        data-testid="chat-conversation-list"
        className="hidden w-56 shrink-0 flex-col border-r border-gray-200 pr-3 sm:flex"
      >
        <button
          type="button"
          data-testid="chat-new"
          className="mb-3 rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white"
          onClick={() => onSelectConversation(null)}
        >
          Neuer Chat
        </button>
        <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <li className="px-2 py-3 text-xs text-gray-400">
              Noch keine Conversations
            </li>
          ) : (
            conversations.map((c) => {
              const active = c.id === activeConversationId;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    data-testid={`chat-conv-${c.id}`}
                    className={`w-full truncate rounded-lg px-2 py-2 text-left text-sm ${
                      active
                        ? "bg-violet-50 font-medium text-violet-700"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                    onClick={() => onSelectConversation(c.id)}
                  >
                    {c.title?.trim() || "Ohne Titel"}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </aside>

      <section className="flex min-w-0 flex-1 flex-col">
        {state === "loading" && bubbles.length === 0 ? (
          <p data-testid="chat-loading" className="text-sm text-gray-500">
            Lade Conversation…
          </p>
        ) : null}

        {state === "error" ? (
          <div
            data-testid="chat-error"
            className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800"
          >
            {errorMessage ?? "Chat konnte nicht geladen werden."}
          </div>
        ) : null}

        {state === "stream_error" ? (
          <div
            data-testid="chat-stream-error"
            className="mb-3 rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          >
            <p>
              {errorMessage ??
                "Verbindung unterbrochen. Nachricht ggf. erneut senden."}
            </p>
            {onRetry ? (
              <button
                type="button"
                data-testid="chat-retry"
                className="mt-2 text-sm font-medium underline"
                onClick={onRetry}
              >
                Erneut versuchen
              </button>
            ) : null}
          </div>
        ) : null}

        {showThread ? (
          <>
            <div
              data-testid="chat-messages"
              className="min-h-0 flex-1 space-y-3 overflow-y-auto pb-4"
            >
              {bubbles.length === 0 ? (
                <div
                  data-testid="chat-empty"
                  className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-6 py-10 text-center"
                >
                  <p className="text-sm font-medium text-gray-700">
                    Frag CaraGo
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    Kurzaufgaben, Notizen, Drafts — Bulk-Research startet einen
                    Job.
                  </p>
                </div>
              ) : (
                bubbles.map((bubble) => {
                  if (bubble.kind === "user") {
                    return (
                      <div
                        key={bubble.id}
                        data-testid={`chat-bubble-user-${bubble.id}`}
                        className="ml-8 rounded-lg bg-violet-600 px-3 py-2 text-sm text-white"
                      >
                        <p className="whitespace-pre-wrap">{bubble.content}</p>
                      </div>
                    );
                  }
                  if (bubble.kind === "assistant") {
                    return (
                      <div
                        key={bubble.id}
                        data-testid={`chat-bubble-assistant-${bubble.id}`}
                        className="mr-8 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800"
                      >
                        <p className="whitespace-pre-wrap">
                          {bubble.content}
                          {bubble.streaming ? (
                            <span className="ml-0.5 text-violet-500">▍</span>
                          ) : null}
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div
                      key={bubble.id}
                      data-testid={`chat-tool-${bubble.id}`}
                      className="mr-8 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
                    >
                      <p className="text-xs font-semibold tracking-wide text-gray-400 uppercase">
                        Tool · {bubble.name}
                        {bubble.ok ? "" : " · fehlgeschlagen"}
                      </p>
                      {bubble.researchJobId ? (
                        <div
                          data-testid={`chat-job-card-${bubble.researchJobId}`}
                          className="mt-2 rounded-md border border-violet-100 bg-violet-50 px-3 py-2"
                        >
                          <p className="text-sm font-medium text-violet-900">
                            Research Job gestartet
                          </p>
                          <Link
                            to={researchJobPath(bubble.researchJobId)}
                            className="mt-1 inline-block text-sm text-violet-700 underline"
                          >
                            Job öffnen
                          </Link>
                        </div>
                      ) : (
                        <p className="mt-1 line-clamp-4 whitespace-pre-wrap text-gray-600">
                          {bubble.summary}
                        </p>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            <form
              data-testid="chat-composer"
              className="flex gap-2 border-t border-gray-200 pt-3"
              onSubmit={(e) => {
                e.preventDefault();
                onSend();
              }}
            >
              <textarea
                data-testid="chat-input"
                rows={2}
                className="min-h-[2.75rem] flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none focus:border-violet-500 disabled:bg-gray-50"
                placeholder="Nachricht…"
                value={draft}
                disabled={sending}
                onChange={(e) => onDraftChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    onSend();
                  }
                }}
              />
              <button
                type="submit"
                data-testid="chat-send"
                disabled={sending || !draft.trim()}
                className="self-end rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
              >
                {sending ? "…" : "Senden"}
              </button>
            </form>
          </>
        ) : null}
      </section>
    </div>
  );
}
